import { DateHelper, filterClasses } from "@swivl-mono/helpers";
import { FeatureFlagKey, TicketActivityType, TicketPreview, TicketStatus, formatTicketNumberDisplay } from "@swivl-mono/types";
import React from "react";

import { isFeatureEnabled } from "../../../Helpers/FeatureFlags/isFeatureEnabled";
import { useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";

type TicketListItemProps = {
  ticket: TicketPreview;
  isSelected: boolean;
  onClick: () => void;
  /** Claim this open-to-group ticket (the parent then routes to My Inbox). */
  onAccept: () => void;
  /** Anonymous access mode: greyed unless selected. */
  locked?: boolean;
  /** Anonymous access mode: never show Accept, even when selected. */
  hideAccept?: boolean;
};

export const statusConfig: Record<TicketStatus, { label: string; bg: string; text: string }> = {
  [TicketStatus.TODO]: { label: "To Do", bg: "bg-[#e6e6e6]", text: "text-black" },
  [TicketStatus.IN_PROGRESS]: { label: "In Progress", bg: "bg-[#fffbe6]", text: "text-[#ffb801]" },
  [TicketStatus.COMPLETED]: { label: "Completed", bg: "bg-[#edf9f8]", text: "text-[#01cbc0]" },
  [TicketStatus.ON_HOLD]: { label: "On Hold", bg: "bg-[#fdeffb]", text: "text-[#ca00b0]" },
  [TicketStatus.CANCELLED]: { label: "Canceled", bg: "bg-[#e6e6e6]", text: "text-[#707070]" }
};

export function TicketListItem({ ticket, isSelected, onClick, onAccept, locked, hideAccept }: TicketListItemProps) {
  const acceptPending = useTicketingStore((s) => !!s.pendingByKey[`accept:${ticket.id}`]);
  const myGroups = useTicketingStore((s) => s.myGroups);

  const lastAt = ticket.lastActivityAt ?? ticket.updatedAt;
  const unread = !!ticket.hasUnreadActivity;
  const statusCfg = statusConfig[ticket.status];

  // Unclaimed, routed-to-a-group tickets show the dispatch subtitle + an
  // inline Accept (claim) button in place of the status/time block.
  const isOpenForAcceptance = ticket.escalationState === "open-to-group" && !!ticket.routingGroupSlug;
  // With the ticketing-groups flag off, Members have no group context, so the
  // row must not name a group — show a neutral status instead of the dispatch line.
  const groupsEnabled = isFeatureEnabled(FeatureFlagKey.TicketingGroups);
  // You can only accept a ticket in a group you belong to (the backend enforces
  // this too). In broad views like All Tickets you can *see* other groups'
  // open-to-group tickets, but the Accept button must not appear on them. With
  // groups off there's no group context, so fall back to the open-state check.
  const isMemberOfGroup = myGroups.some((g) => g.slug === ticket.routingGroupSlug);
  const canAccept = !hideAccept && isOpenForAcceptance && (!groupsEnabled || isMemberOfGroup);

  const handleAccept = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onAccept();
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
      role="button"
      tabIndex={0}
      className={filterClasses(
        "w-full rounded-lg p-2.5 text-left transition",
        locked ? "cursor-not-allowed text-grey-500" : "cursor-pointer",
        isSelected ? "bg-grey-50" : locked ? "bg-white" : "bg-white hover:bg-grey-50"
      )}
    >
      <div className="flex items-center gap-2">
        {/* Left content */}
        <div className="min-w-0 flex-1">
          {/* Title row with unread indicator and ticket number */}
          <div className="flex items-center gap-1 overflow-hidden">
            {unread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />}
            <span className={filterClasses("body-sm-alt min-w-0 truncate", locked ? "text-grey-500" : "text-black")}>{ticket.title}</span>
            <span className="heading-mono shrink-0 text-grey-600">#{formatTicketNumberDisplay(ticket.number)}</span>
          </div>

          {/* Subtitle - dispatch line for unclaimed group tickets, else last activity */}
          <div className="body-sm text-grey-600">
            {isOpenForAcceptance
              ? groupsEnabled
                ? `Dispatched to ${ticket.routingGroupSlug} group`
                : "Awaiting acceptance"
              : ticket.lastActivityType
                ? formatActivityType(ticket.lastActivityType)
                : "No activity yet"}
          </div>
        </div>

        {/* Right content */}
        {canAccept ? (
          <button
            type="button"
            onClick={handleAccept}
            disabled={acceptPending}
            className="body-sm-alt flex h-[26px] shrink-0 cursor-pointer items-center rounded border border-grey-300 bg-white px-3 text-black transition hover:bg-grey-50 disabled:cursor-default disabled:opacity-60"
          >
            {acceptPending ? "Accepting…" : "Accept"}
          </button>
        ) : (
          <div className="flex shrink-0 flex-col items-end">
            {/* Status badge */}
            <div className="flex items-center gap-1">
              <span className={["body-xs inline-flex h-[18px] items-center justify-center rounded-sm px-1", statusCfg.bg, statusCfg.text].join(" ")}>
                {statusCfg.label}
              </span>
            </div>

            {/* Time */}
            {lastAt && <span className="body-sm text-grey-500">{DateHelper.timeAgoShort(lastAt as any)}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

const activityDescriptions: Record<TicketActivityType, string> = {
  [TicketActivityType.TICKET_CREATED]: "Ticket created",
  [TicketActivityType.STATUS_CHANGED]: "Status updated",
  [TicketActivityType.PRIORITY_CHANGED]: "Priority changed",
  [TicketActivityType.TITLE_CHANGED]: "Title updated",
  [TicketActivityType.DESCRIPTION_CHANGED]: "Description updated",
  [TicketActivityType.TAGS_CHANGED]: "Tags updated",
  [TicketActivityType.ASSIGNEES_CHANGED]: "Assignee changed",
  [TicketActivityType.USER_CHANGED]: "Customer changed",
  [TicketActivityType.DUE_DATE_CHANGED]: "Due date changed",
  [TicketActivityType.LOCATION_CHANGED]: "Location changed",
  [TicketActivityType.NOTE_ADDED]: "Note added",
  [TicketActivityType.NOTE_EDITED]: "Note edited",
  [TicketActivityType.NOTE_DELETED]: "Note deleted",
  [TicketActivityType.TASK_ADDED]: "Task added",
  [TicketActivityType.TASK_DELETED]: "Task deleted",
  [TicketActivityType.TASK_EDITED]: "Task edited",
  [TicketActivityType.TASK_STATE_CHANGED]: "Task completed",
  [TicketActivityType.TASK_ACTION_UPDATED]: "Task action updated",
  [TicketActivityType.TASKS_REORDERED]: "Tasks reordered",
  [TicketActivityType.TICKET_CLAIMED]: "Ticket accepted",
  [TicketActivityType.TICKET_ESCALATED]: "Bot escalated this ticket",
  [TicketActivityType.TICKET_DISPATCHED]: "Dispatched to group",
  [TicketActivityType.BOT_UPDATED_TICKET]: "Updated by bot"
};

function formatActivityType(type: string): string {
  return activityDescriptions[type as TicketActivityType] ?? type.replace(/_/g, " ");
}
